import * as React from 'react';
import { useContext, useMemo } from 'react';

import { useAccount } from '@hooks/useAccount';
import { useFeature } from '@hooks/useFeature';
import { useTeams } from '@hooks/useTeams';
import { useUser } from '@hooks/useUser';
import { store as permissionsStore } from '@stores/permissions';

import { DropdownProps } from '../../types';

import { MultiSelectWithSearch, Option } from './MultiSelectWithSearch';

type Props<D = any> = DropdownProps<D, number[]>;

export const Teams: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const { teams: allTeams } = useTeams();
  const user = useUser();
  const { account } = useAccount();
  const hasTeamAccessControl = useFeature('team_access_control');
  const { role } = useContext(permissionsStore);

  const userTeams = useMemo(
    () => allTeams?.filter((team) => user.teams?.map((t) => t.id).includes(team.id)),
    [allTeams, user.teams]
  );

  const shouldFilterTeams = useMemo(() => {
    // If team_access_control flag is disabled, show all teams
    if (!hasTeamAccessControl) return false;

    // If visibility_permission_condition is 'unconditional_allow', show all teams
    if (account.visibility_permission_condition === 'unconditional_allow') return false;

    // If user is admin, show all teams
    if (role === 'admin') return false;

    // If team_access_control is enabled and visibility_permission_condition is 'allow_if_team_match'
    // and user is creator or observer, filter to userTeams only
    if (
      account.visibility_permission_condition === 'allow_if_team_match' &&
      (role === 'creator' || role === 'observer')
    ) {
      return true;
    }

    return false;
  }, [hasTeamAccessControl, account.visibility_permission_condition, role]);

  const filteredTeams = shouldFilterTeams ? userTeams : allTeams;

  const options: Option[] = (filteredTeams || []).map((t) => ({
    label: t.name,
    value: t.id
  }));

  return <MultiSelectWithSearch {...props} options={options} />;
};
